import { DISTRICT_SPAWN, PLOTS } from './district'
import { INTERIORS } from './interiors'

export interface WorldDestination {
  id: string
  label: string
  description: string
  plotId: string | null
  x: number
  z: number
  yaw: number
}

/**
 * Arena owns world coordinates. The Portal only publishes application data;
 * it never needs to know where a room or building lives in the 3D scene.
 */
export const WORLD_DESTINATIONS: readonly WorldDestination[] = [
  {
    id: 'district',
    label: 'Animus Quarter',
    description: 'High Street and every building entrance',
    plotId: null,
    ...DISTRICT_SPAWN,
  },
  ...INTERIORS.map((interior) => {
    const plot = PLOTS.find((candidate) => candidate.id === interior.plotId)
    if (!plot) throw new Error(`Interior ${interior.plotId} has no district plot`)
    return {
      id: `interior:${interior.plotId}`,
      label: plot.name,
      description: destinationDescription(interior.plotId),
      plotId: interior.plotId,
      ...interior.spawn,
    }
  }),
]

function destinationDescription(plotId: string): string {
  switch (plotId) {
    case 'P1': return 'Decisions, priorities, and planning'
    case 'P2': return 'Live operations and active work'
    case 'P3': return 'Your agents and shared workplace'
    case 'P4': return 'Metrics and marketplace signals'
    case 'P5': return 'Workflows, tools, and experiments'
    case 'P6': return 'Informal conversations and breaks'
    case 'P7': return 'Knowledge, subjects, and research'
    case 'P8': return 'World and application infrastructure'
    default: return 'Animus Quarter destination'
  }
}

type TeleportHandler = (destination: WorldDestination) => void

let activeHandler: TeleportHandler | null = null

/** The mounted world registers its one canonical transition implementation. */
export function registerWorldTeleportHandler(handler: TeleportHandler): () => void {
  activeHandler = handler
  return () => {
    if (activeHandler === handler) activeHandler = null
  }
}

/** Resolve an opaque place id and ask the mounted world to move the player. */
export function requestWorldTeleport(destinationId: string): boolean {
  const destination = WORLD_DESTINATIONS.find((candidate) => candidate.id === destinationId)
  if (!destination || !activeHandler) return false
  activeHandler(destination)
  return true
}

/** Resolve a server-authorized room id to an Arena-owned safe spawn anchor. */
export function worldDestinationForRoom(roomId: string): WorldDestination | undefined {
  if (roomId === 'district') return WORLD_DESTINATIONS.find((candidate) => candidate.id === 'district')
  const plot = roomId === 'office' || roomId === 'hq'
    ? PLOTS.find((candidate) => candidate.id === 'P3')
    : PLOTS.find((candidate) => candidate.domain === roomId)
  return plot
    ? WORLD_DESTINATIONS.find((candidate) => candidate.plotId === plot.id)
    : undefined
}
